import { cn } from '@/utils'
import { ReactNode, useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useSearchParams } from 'wouter'

import { useCADPageStore } from '@/state/cad'
import type { Step } from '@/services/queries/operation_steps/types'
import { useCadPageParams } from '../../hooks'
import { GLTFObject } from '@/lib/cad/GLTFObject'
import { useDroppable } from '@dnd-kit/core'
import { DRAGGABLE_TYPE as DRAGGABLE_ASSEMBLY_TREE_NODE_TYPE } from '../AssemblyTree/constants'

export const StepCardContainer = ({
  children,
  step,
  stepNumber,
  gltf,
  onSelect,
}: {
  children: ReactNode
  step: Step
  stepNumber: number
  gltf?: GLTFObject
  isReadOnly?: boolean
  onSelect?: (e: React.MouseEvent) => void
}) => {
  const { documentId } = useCadPageParams()
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const activeOperationStep = useCADPageStore((state) => state.operationStep)
  const isActive = activeOperationStep?.stepId === step.id
  const selectFromCad = isActive && activeOperationStep?.selectFromCad
  const colorMap = useCADPageStore((state) => state.colorMap)
  const [searchParams, setSearchParams] = useSearchParams()
  const { active, isOver, setNodeRef } = useDroppable({
    id: `step-card-drop-zone-${stepNumber}`,
    data: { type: 'step-card-drop-zone', step },
  })

  const dragType = useMemo(
    () => active?.data?.current?.type as string | undefined,
    [active],
  )
  const isDroppable = useMemo(
    () => dragType === DRAGGABLE_ASSEMBLY_TREE_NODE_TYPE,
    [dragType],
  )

  const updateQueryParams = useCallback(
    (newParams: Record<string, string | null | undefined>): void => {
      const updatedSearchParams = new URLSearchParams(searchParams)

      Object.entries(newParams).forEach(([key, value]) => {
        if (value == null) {
          updatedSearchParams.delete(key)
        } else {
          updatedSearchParams.set(key, value)
        }
      })

      setSearchParams(updatedSearchParams)
    },
    [searchParams, setSearchParams],
  )

  return (
    <div
      className={cn(
        'border border-gray-300 border rounded-lg p-3 bg-white text-sm cursor-pointer',
        {
          'bg-primary-10': isActive,
          'border-primary-50': isActive,
          'bg-purple-50': selectFromCad,
          'border-purple-600': selectFromCad,
          'bg-teal-50': isDroppable && isOver,
          'border-teal-600': isDroppable && isOver,
        },
      )}
      id={'step-' + step.id}
      ref={setNodeRef}
      // Use click instead of mousedown so dragging does not trigger a store
      // write that can rerender and reset SortableList during drag start.
      onClick={(e) => {
        onSelect?.(e)
        if (isActive) {
          updateQueryParams({ page_id: null })
          setCadPageState({
            operationStep: null,
          })
          return
        }
        gltf?.unhighlightParts(colorMap)
        updateQueryParams({ page_id: step.id as string })
        setCadPageState({
          operationStep: {
            documentId,
            stepId: step.id as string,
            selectFromCad: false,
            isActive: Boolean(isActive),
          },
        })
      }}
      data-testid={`step-${stepNumber}-card-drop-zone`}
    >
      {children}
    </div>
  )
}
